import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { isNotEmpty, useForm } from '@mantine/form';
import {
    createPayoutCascadesUrl,
    isNewPayoutCascadeEntityId,
    type PayoutTrader,
    useCreatePayoutCascadeGroup,
    useDeletePayoutCascadeGroup,
    usePayoutCascadeGroup,
    usePayoutTradersList,
    useUpdatePayoutCascadeGroup,
} from '~/entities/payout';

interface CascadeGroupEditorForm {
    name: string;
    capacity: number;
    overflowCapacity: number;
    traderIds: string[];
}

export const useCascadeGroupEditor = (id?: string) => {
    const navigate = useNavigate();
    const isNew = isNewPayoutCascadeEntityId(id);

    const group = usePayoutCascadeGroup({ 'id.value': id ?? '' });
    const traders = usePayoutTradersList({
        'pagination.page': '0',
        'pagination.perPage': '9999',
    });

    const [isLoaded, setIsLoaded] = useState(isNew);

    const form = useForm<CascadeGroupEditorForm>({
        initialValues: {
            name: '',
            capacity: 0,
            overflowCapacity: 0,
            traderIds: [],
        },
        validate: {
            name: isNotEmpty('Заполните поле'),
        },
    });

    useEffect(() => {
        if (isLoaded || !group.data?.group) {
            return;
        }

        const loadedGroup = group.data.group;

        form.setValues({
            name: loadedGroup.name?.value ?? '',
            capacity: loadedGroup.queue?.capacity ?? 0,
            overflowCapacity: loadedGroup.queue?.overflowCapacity ?? 0,
            traderIds:
                loadedGroup.traderIds
                    ?.map((traderId) => traderId.value)
                    .filter((value): value is string => Boolean(value)) ?? [],
        });
        setIsLoaded(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoaded, group.data?.group]);

    const tradersOptions = (traders.data?.items ?? [])
        .filter((trader): trader is PayoutTrader & { id: { value: string } } => Boolean(trader.id?.value))
        .map((trader) => ({
            value: trader.id.value,
            label: trader.traderName?.value || trader.id.value,
        }));

    const create = useCreatePayoutCascadeGroup({
        onSuccess: () => navigate(createPayoutCascadesUrl('groups')),
    });

    const update = useUpdatePayoutCascadeGroup();

    const deleteGroup = useDeletePayoutCascadeGroup({
        onSuccess: () => navigate(createPayoutCascadesUrl('groups')),
    });

    const handleSubmit = form.onSubmit((values) => {
        const payload = {
            name: { value: values.name },
            queueSettings: {
                capacity: values.capacity,
                overflowCapacity: values.overflowCapacity,
            },
            traderIds: values.traderIds.map((value) => ({ value })),
        };

        if (isNew) {
            create.mutate(payload);

            return;
        }

        update.mutate({ id: { value: id }, ...payload });
    });

    const handleDelete = () => {
        if (!id) {
            return;
        }

        deleteGroup.mutate({ 'id.value': id });
    };

    return {
        isNew,
        isLoading: !isNew && group.isPending,
        form,
        tradersOptions,
        isTradersLoading: traders.isPending,
        currentTraders: group.data?.group?.traders,
        handleSubmit,
        handleDelete,
        isSubmitPending: create.isPending || update.isPending,
        isDeletePending: deleteGroup.isPending,
    };
};
